/**
 * Admin route path constants
 *
 * Centralized path definitions to avoid hardcoded strings and make
 * refactoring easier. Use these constants instead of string literals
 * when calling revalidatePath() or building navigation URLs.
 *
 * @example
 * ```typescript
 * import { ADMIN_PATHS } from '../_lib/constants';
 *
 * // In actions
 * revalidatePath(ADMIN_PATHS.ACCOUNTS.LIST);
 * revalidatePath(ADMIN_PATHS.ACCOUNTS.DETAIL(accountId));
 *
 * // In navigation
 * router.push(ADMIN_PATHS.EVENTS.CREATE);
 * ```
 */

export const ADMIN_PATHS = {
  ROOT: '/admin',

  ACCOUNTS: {
    LIST: '/admin/accounts',
    DETAIL: (id: string) => `/admin/accounts/${id}` as const,
  },

  CLIENTS: {
    LIST: '/admin/clients',
    DETAIL: (id: string) => `/admin/clients/${id}` as const,
  },

  EVENTS: {
    LIST: '/admin/events',
    DETAIL: (id: string) => `/admin/events/${id}` as const,
    CREATE: '/admin/events/create',
  },

  PRODUCTIONS: {
    LIST: '/admin/productions',
    DETAIL: (id: string) => `/admin/productions/${id}` as const,
    CREATE: '/admin/productions/create',
  },

  DANCERS: {
    LIST: '/admin/artists',
    DETAIL: (id: string) => `/admin/artists/${id}` as const,
  },

  SYNC: {
    ROOT: '/admin/sync',
    LOGS: '/admin/sync/logs',
  },
} as const;

/**
 * Helper to get all paths that should be revalidated when account changes
 */
export function getAccountRelatedPaths(accountId?: string): string[] {
  const paths: string[] = [
    ADMIN_PATHS.ROOT,
    ADMIN_PATHS.ACCOUNTS.LIST,
    ADMIN_PATHS.CLIENTS.LIST,
    ADMIN_PATHS.EVENTS.LIST,
    ADMIN_PATHS.PRODUCTIONS.LIST,
  ];

  if (accountId) {
    paths.push(ADMIN_PATHS.ACCOUNTS.DETAIL(accountId));
  }

  return paths;
}

/**
 * Helper to revalidate all account-related paths
 *
 * @example
 * ```typescript
 * import { revalidatePath } from 'next/cache';
 * import { revalidateAccountPaths } from '../_lib/constants';
 *
 * await updateAccountAction(accountId, data);
 * revalidateAccountPaths(accountId); // Revalidates all related paths
 * ```
 */
export function revalidateAccountPaths(
  revalidate: typeof import('next/cache').revalidatePath,
  accountId?: string,
): void {
  const paths = getAccountRelatedPaths(accountId);
  paths.forEach((path) => revalidate(path));
}

/**
 * Pagination configuration
 */
export const PAGINATION = {
  /** Default page size for tables and lists */
  DEFAULT_PAGE_SIZE: 20,
  /** Options available in page size selector */
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100] as const,
  /** Default page number for initial load */
  DEFAULT_PAGE: 1,
} as const;

/**
 * Search and filtering configuration
 */
export const SEARCH = {
  /** Minimum characters required to trigger search */
  MIN_SEARCH_LENGTH: 3,
  /** Debounce delay in milliseconds for search input */
  DEBOUNCE_DELAY: 300,
  /** Maximum search results to display */
  MAX_SEARCH_RESULTS: 50,
} as const;

/**
 * Cast management configuration
 */
export const CAST = {
  /** Maximum number of suggested dancers to show based on history */
  MAX_SUGGESTED_DANCERS: 5,
  /** Default assignment status for new cast members */
  DEFAULT_ASSIGNMENT_STATUS: 'offered' as const,
} as const;

/**
 * Data export configuration
 */
export const EXPORT = {
  /** Maximum number of records to fetch for CSV export */
  MAX_EXPORT_PAGE_SIZE: 10000,
} as const;

/**
 * UI configuration
 */
export const UI = {
  /** Toast notification duration in milliseconds */
  TOAST_DURATION: 5000,
  /** Animation duration for transitions */
  ANIMATION_DURATION: 200,
  /** Debounce delay for auto-save features */
  AUTO_SAVE_DELAY: 1000,
} as const;
